import { useState, useEffect, useRef } from "react";
import { MdDashboard, MdOutlineNoteAlt } from "react-icons/md";
import { FiClipboard } from "react-icons/fi";
import taskIcon from "../assets/new-images/task-icon.svg";
import noteIcon from "../assets/new-images/note-svg.svg";
import { FaUsers } from "react-icons/fa";
import { LuFileText } from "react-icons/lu";
import { motion } from "motion/react";
import { createIcons, lensConcave } from "lucide";
import { BsLightningChargeFill } from "react-icons/bs";

import dataGif from "../../src/assets/new-images/Tasks6.mp4";
import dataNotes from "../../src/assets/new-images/notes4.mp4";
import DashboardGif from "../../src/assets/new-images/Dashboard3.mp4";
import QuickAction from "../../src/assets/new-images/quick-actions.mp4";
import dataDocument from "../../src/assets/new-images/Document-2.gif";
import dataTeams from "../../src/assets/new-images/collab.png";

const tabs = [
  { label: "Dashboards",    icon: <MdDashboard className="w-4 h-4" />,      gif: DashboardGif },
  { label: "Tasks",         icon: <img src={taskIcon} style={{ width: 16, height: 16, minWidth: 16 }} alt="Tasks" />, gif: dataGif      },
  { label: "Notes",         icon: <img src={noteIcon} style={{ width: 16, height: 16, minWidth: 16 }} alt="Notes" />, gif: dataNotes    },
  { label: "Quick Actions", icon: <BsLightningChargeFill className="w-4 h-4" style={{ color: "#093a5b" }} />, gif: QuickAction  },
];

export default function TabSwitcher() {
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const videoRef = useRef(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [lockedHeight, setLockedHeight] = useState(null);
  const videoAreaRef = useRef(null);
  const isFirstLoad = useRef(true);
  const knownHeight = useRef(null);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      if (videoRef.current) {
        videoRef.current.load();
        videoRef.current.play().catch(() => {});
      }
      return;
    }
    // Use the known height measured after first video loaded
    if (knownHeight.current) {
      setLockedHeight(knownHeight.current);
    }
    setIsTransitioning(true);
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [activeTab]);

  const containerMaxWidth = windowWidth > 1440 ? "1400px" : windowWidth > 1280 ? "1160px" : windowWidth > 768 ? "940px" : "100%";

  return (
    <motion.div
      className="w-full flex flex-col items-center"
      style={{ paddingTop: windowWidth <= 640 ? "1.5rem" : "3.5rem" }}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: "easeOut" }}
    >
      <div className="w-full" style={{ maxWidth: containerMaxWidth, padding: windowWidth <= 768 ? "0 16px" : "0 48px" }}>

        {/* ── Outer frame — subtle dark border, premium feel ── */}
        <div style={{
          border: "1.5px solid rgba(15,23,42,0.14)",
          borderBottom: "none",
          borderRadius: "16px 16px 0 0",
          background: "transparent",
          boxShadow: `
            0 2px 4px rgba(0,0,0,0.04),
            0 8px 20px rgba(0,0,0,0.07),
            0 24px 52px rgba(9,58,91,0.1),
            0 48px 96px rgba(9,58,91,0.08)
          `,
        }}>

          {/* Inner white card — chrome bar + video */}
          <div style={{ borderRadius: "14px 14px 0 0", overflow: "hidden", background: "#fff" }}>

          {/* Browser chrome bar */}
          <div style={{
            background: "#1e293b",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            padding: windowWidth <= 640 ? "3px 10px" : "11px 16px",
            display: "flex",
            alignItems: "center",
            gap: windowWidth <= 640 ? "5px" : "7px",
          }}>
            <span style={{ width: windowWidth <= 640 ? 6 : 12, height: windowWidth <= 640 ? 6 : 12, borderRadius: "50%", background: "#ff5f57", display: "inline-block" }} />
            <span style={{ width: windowWidth <= 640 ? 6 : 12, height: windowWidth <= 640 ? 6 : 12, borderRadius: "50%", background: "#febc2e", display: "inline-block" }} />
            <span style={{ width: windowWidth <= 640 ? 6 : 12, height: windowWidth <= 640 ? 6 : 12, borderRadius: "50%", background: "#28c840", display: "inline-block" }} />
            <div style={{ flex: 1, margin: windowWidth <= 640 ? "0 8px" : "0 14px", background: "rgba(255,255,255,0.08)", borderRadius: "6px", height: windowWidth <= 640 ? "12px" : "22px", display: "flex", alignItems: "center", paddingLeft: windowWidth <= 640 ? "7px" : "10px" }}>
              <span style={{ fontSize: windowWidth <= 640 ? "6px" : "11px", color: "rgba(255,255,255,0.5)", fontFamily: "monospace" }}>app.simplifyte.com</span>
            </div>
          </div>

          {/* Video area */}
          <style>{`
            @keyframes tabShimmer {
              0%   { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}</style>
          <div
            ref={videoAreaRef}
            style={{
              position: "relative",
              overflow: "hidden",
              background: "#ffffff",
              WebkitMaskImage: "linear-gradient(to bottom, black 85%, transparent 100%)",
              maskImage: "linear-gradient(to bottom, black 85%, transparent 100%)",
              minHeight: lockedHeight ? `${lockedHeight}px` : undefined,
            }}
          >
            {/* Shimmer overlay during transition — sits on top, fades out when video ready */}
            {isTransitioning && (
              <div style={{
                position: "absolute", inset: 0, zIndex: 2,
                background: "linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)",
                backgroundSize: "200% 100%",
                animation: "tabShimmer 1.2s infinite",
              }} />
            )}
            {activeTab.gif.endsWith(".mp4") ? (
              <video
                ref={videoRef}
                src={activeTab.gif}
                autoPlay loop muted playsInline
                onCanPlay={(e) => {
                  // Measure real rendered height and store it
                  if (videoAreaRef.current && videoAreaRef.current.offsetHeight > 50) {
                    knownHeight.current = videoAreaRef.current.offsetHeight;
                  }
                  setLockedHeight(null);
                  setIsTransitioning(false);
                }}
                className="w-full h-auto object-contain block"
                style={{ opacity: isTransitioning ? 0 : 1, transition: "opacity 0.3s ease" }}
              />
            ) : (
              <img
                src={activeTab.gif}
                alt={activeTab.label}
                onLoad={(e) => {
                  if (videoAreaRef.current && videoAreaRef.current.offsetHeight > 50) {
                    knownHeight.current = videoAreaRef.current.offsetHeight;
                  }
                  setLockedHeight(null);
                  setIsTransitioning(false);
                }}
                className="w-full h-auto object-contain block"
                style={{ opacity: isTransitioning ? 0 : 1, transition: "opacity 0.3s ease" }}
              />
            )}
          </div>{/* end video area */}
          </div>{/* end inner white card */}
        </div>{/* end outer frame */}

      </div>{/* end maxWidth container */}


      {/* ── Tab strip ── */}
      <div style={{
        display: "flex",
        justifyContent: windowWidth <= 768 ? "flex-start" : "center",
        flexWrap: "nowrap",
        overflowX: windowWidth <= 768 ? "auto" : "visible",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
        gap: "0",
        padding: windowWidth <= 768 ? "6px 16px" : "8px 24px",
        width: "100%",
        maxWidth: windowWidth <= 768 ? "100vw" : undefined,
      }}>
        {tabs.map((tab) => {
          const isActive = activeTab.label === tab.label;
          return (
            <button
              key={tab.label}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: windowWidth <= 768 ? "10px 14px" : "10px 22px",
                fontSize: windowWidth <= 768 ? "12px" : "13px",
                fontWeight: isActive ? 600 : 400,
                color: isActive ? "#093a5b" : "#64748b",
                background: "transparent",
                border: "none",
                borderBottom: isActive ? "2px solid #093a5b" : "2px solid transparent",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "7px",
                transition: "color 0.2s, border-color 0.2s",
                whiteSpace: "nowrap",
                outline: "none",
              }}
              onMouseEnter={e => {
                if (!isActive) e.currentTarget.style.color = "#093a5b";
              }}
              onMouseLeave={e => {
                if (!isActive) e.currentTarget.style.color = "#64748b";
              }}
            >
              <span style={{ display: "flex", alignItems: "center", opacity: isActive ? 1 : 0.6 }}>{tab.icon}</span>
              {tab.label}
            </button>
          );
        })}
      </div>

    </motion.div>
  );
}
